/**
 * Lap and checkpoint bookkeeping — the rules of "did that lap count?", kept free
 * of Three.js and the DOM so they can be unit-tested.
 *
 * The geometry (which gate did the kart cross this frame?) lives in track.ts;
 * this module only consumes the resulting gate indices. Feeding it a crossing
 * returns the events worth telling the driver about, which is the whole point:
 * a checkpoint you miss has to announce itself the moment it happens, not two
 * corners later when the finish line silently refuses to count the lap.
 *
 * Checkpoint 0 is the start/finish line, so a lap is the sequence
 * 1, 2, ... n-1, 0. Skipping a gate does not block progress — the tracker moves
 * on with the driver — but the skipped gate is remembered, and crossing the line
 * with any still outstanding voids the lap. Driving back through a missed gate
 * clears it, so a mistake is recoverable rather than a dead lap.
 */

/** How one checkpoint should be presented right now. */
export type CheckpointState =
  /** Not reached yet this lap. */
  | 'ahead'
  /** The gate the driver should cross next. */
  | 'next'
  /** Crossed this lap. */
  | 'passed'
  /** Skipped this lap and not yet recovered — this is what voids the lap. */
  | 'missed';

/** Something worth showing the driver, produced by `cross()`. */
export type ProgressEvent =
  /** An ordinary checkpoint went by. `passed`/`total` count gates this lap. */
  | { type: 'checkpoint'; index: number; passed: number; total: number }
  /** Gates were skipped. The lap is void unless they are driven through again. */
  | { type: 'missed'; indices: number[] }
  /** A previously missed gate was collected on the way back. */
  | { type: 'recovered'; index: number; outstanding: number }
  /** A clean lap was completed; `lap` is the new count of finished laps. */
  | { type: 'lap'; lap: number }
  /** The line was crossed with gates outstanding, so the lap did not count. */
  | { type: 'lapVoid'; missed: number }
  /** The final lap was completed. */
  | { type: 'finish' };

/**
 * Sequential gate tracker for one racer.
 *
 * `lastCp` is the last gate actually crossed (what the network and the placement
 * maths want), `nextCp` the one expected next.
 */
export class LapTracker {
  /** Number of gates in the loop, including the start/finish line. */
  readonly count: number;
  private readonly totalLaps: number;

  /** Completed laps. */
  lap = 0;
  /** Index of the gate expected next. */
  nextCp = 1;
  /** Index of the last gate crossed. */
  lastCp = 0;
  /** True once the final lap has been completed. */
  finished = false;

  private readonly state: CheckpointState[];
  /** Gates skipped this lap and not yet driven through. */
  private readonly missed = new Set<number>();

  constructor(count: number, totalLaps: number) {
    if (count < 2) throw new Error('LapTracker needs at least two checkpoints');
    this.count = count;
    this.totalLaps = totalLaps;
    this.state = new Array<CheckpointState>(count).fill('ahead');
  }

  /** Start from the grid: lap 0, nothing passed, nothing missed. */
  reset(): void {
    this.lap = 0;
    this.finished = false;
    this.beginLap();
  }

  /**
   * Resume mid-race from a server-reported position in the loop (a reconnect).
   * Everything up to `cp` is taken to have been passed cleanly, which is what
   * the server was relaying on this player's behalf.
   */
  resume(lap: number, cp: number): void {
    this.lap = Math.max(0, lap);
    this.finished = false;
    this.beginLap();
    const at = ((cp % this.count) + this.count) % this.count;
    for (let i = 1; i <= at; i++) this.state[i] = 'passed';
    this.lastCp = at;
    this.nextCp = (at + 1) % this.count;
  }

  /**
   * Register a gate crossing and return what the driver should be told.
   *
   * Crossings are classified by how far ahead of the expected gate they are:
   * within half a lap counts as forward progress (with anything in between
   * marked missed), and anything else is a gate behind us — which only matters
   * when it is one we owe, i.e. the driver went back for it.
   */
  cross(index: number): ProgressEvent[] {
    if (this.finished) return [];
    const n = this.count;
    if (!Number.isInteger(index) || index < 0 || index >= n) return [];

    const events: ProgressEvent[] = [];
    const ahead = (index - this.nextCp + n) % n;

    // Behind us: only interesting as the recovery of a gate we skipped.
    if (ahead * 2 >= n && ahead !== 0) {
      if (this.missed.delete(index)) {
        this.state[index] = 'passed';
        events.push({ type: 'recovered', index, outstanding: this.missed.size });
      }
      return events;
    }

    // Forward. Anything between the expected gate and this one was skipped.
    if (ahead > 0) {
      const skipped: number[] = [];
      for (let k = 0; k < ahead; k++) {
        const idx = (this.nextCp + k) % n;
        if (this.state[idx] === 'passed' || this.missed.has(idx)) continue;
        this.missed.add(idx);
        this.state[idx] = 'missed';
        skipped.push(idx);
      }
      if (skipped.length > 0) events.push({ type: 'missed', indices: skipped });
    }

    this.missed.delete(index);
    this.state[index] = 'passed';
    this.lastCp = index;
    this.nextCp = (index + 1) % n;

    if (index !== 0) {
      events.push({ type: 'checkpoint', index, passed: this.passedCount(), total: n - 1 });
      return events;
    }

    // The start/finish line closes the lap out.
    const outstanding = this.missed.size;
    if (outstanding > 0) {
      events.push({ type: 'lapVoid', missed: outstanding });
    } else {
      this.lap += 1;
      if (this.lap >= this.totalLaps) {
        this.finished = true;
        events.push({ type: 'finish' });
      } else {
        events.push({ type: 'lap', lap: this.lap });
      }
    }
    if (!this.finished) this.beginLap();
    return events;
  }

  /**
   * Presentation state per checkpoint index. The gate expected next is reported
   * as 'next' so it can be highlighted on track, in the HUD and on the minimap.
   */
  states(): CheckpointState[] {
    const out = this.state.slice();
    if (!this.finished && out[this.nextCp] !== 'passed') out[this.nextCp] = 'next';
    return out;
  }

  /** Gates still owed for this lap to count. */
  get outstanding(): number {
    return this.missed.size;
  }

  /** How many of this lap's gates (excluding the line) have been crossed. */
  private passedCount(): number {
    let count = 0;
    for (let i = 1; i < this.count; i++) {
      if (this.state[i] === 'passed') count++;
    }
    return count;
  }

  /** Wipe the per-lap slate: every gate ahead of us again, nothing owed. */
  private beginLap(): void {
    this.state.fill('ahead');
    this.missed.clear();
    this.lastCp = 0;
    this.nextCp = 1;
  }
}
